/**
 * Durable link between questions and Setup-tab shared scale presets.
 *
 * Questions store `display_config.scalePresetId` when a named preset is applied.
 * Matching by id keeps the link when labels/range change; Setup persists also
 * re-copies scale fields onto linked questions so take/scoring stay in sync.
 */
import type { AeQuestionDisplayConfig, AeTemplateDetail, AeTemplateQuestion } from '@/types/assessmentEngine';
import type { QuestionDraft } from './questionDraftTypes';
import type { AeTemplateScalePreset } from './templateConfigTypes';
import {
  defaultMatrixConfig,
  defaultNpsConfig,
  defaultScaleConfig,
  DEFAULT_SCALE_MAX_LABEL,
  DEFAULT_SCALE_MIN_LABEL,
} from './scaleUtils';
import { parseDisplayConfig } from './questionTypeConfig';
import { assessmentEngineService } from '@/services/assessmentEngineService';

function effectiveValueLabels(labels?: string[]): string[] {
  const cleaned = (labels ?? []).map((label) => (label ?? '').trim());
  return cleaned.every((label) => !label) ? [] : cleaned;
}

export function applyScalePreset(preset: AeTemplateScalePreset): Partial<QuestionDraft> {
  if (preset.matrixShared) {
    return applyMatrixScalePreset(preset);
  }
  if (preset.scale_min === 0 && preset.scale_max === 10) {
    return {
      question_type: 'nps',
      scale_min: 0,
      scale_max: 10,
      allow_na: preset.allow_na,
      display_config: {
        ...defaultNpsConfig(),
        scaleDisplayStyle: preset.scaleDisplayStyle ?? 'numbers',
        scaleMinLabel: preset.scaleMinLabel?.trim() || defaultNpsConfig().scaleMinLabel,
        scaleMaxLabel: preset.scaleMaxLabel?.trim() || defaultNpsConfig().scaleMaxLabel,
        scaleValueLabels: preset.scaleValueLabels,
        scalePresetId: preset.id,
      },
    };
  }
  const len = preset.scale_max - preset.scale_min + 1;
  return {
    question_type: 'scale',
    scale_min: preset.scale_min,
    scale_max: preset.scale_max,
    allow_na: preset.allow_na,
    display_config: {
      ...defaultScaleConfig(),
      scaleLength: len,
      scaleDisplayStyle: preset.scaleDisplayStyle ?? 'radio',
      scaleMinLabel: preset.scaleMinLabel?.trim() || DEFAULT_SCALE_MIN_LABEL,
      scaleMaxLabel: preset.scaleMaxLabel?.trim() || DEFAULT_SCALE_MAX_LABEL,
      scaleValueLabels: preset.scaleValueLabels,
      scalePresetId: preset.id,
    },
  };
}

/** Apply a shared scale as matrix columns while leaving matrix rows question-specific. */
export function applyMatrixScalePreset(preset: AeTemplateScalePreset): Partial<QuestionDraft> {
  const len = preset.scale_max - preset.scale_min + 1;
  return {
    question_type: 'matrix',
    scale_min: preset.scale_min,
    scale_max: preset.scale_max,
    allow_na: preset.allow_na,
    display_config: {
      ...defaultMatrixConfig(),
      scaleLength: len,
      scaleDisplayStyle: 'radio',
      scaleMinLabel: preset.scaleMinLabel?.trim() || DEFAULT_SCALE_MIN_LABEL,
      scaleMaxLabel: preset.scaleMaxLabel?.trim() || DEFAULT_SCALE_MAX_LABEL,
      scaleValueLabels: preset.scaleValueLabels,
      scalePresetId: preset.id,
    },
  };
}

/**
 * Match a Setup-tab scale preset by range / N/A / endpoint labels / value labels.
 * Used only as a legacy fallback when scalePresetId is missing.
 * Display style is editable per question, so it is ignored here.
 */
export function presetMatchesDraftValues(
  preset: AeTemplateScalePreset,
  draft: Pick<QuestionDraft, 'question_type' | 'scale_min' | 'scale_max' | 'allow_na' | 'display_config'>
): boolean {
  const targetType = preset.matrixShared
    ? 'matrix'
    : preset.scale_min === 0 && preset.scale_max === 10
      ? 'nps'
      : 'scale';
  const draftValueLabels = effectiveValueLabels(draft.display_config.scaleValueLabels);
  const presetValueLabels = effectiveValueLabels(preset.scaleValueLabels);
  const valueLabelsMatch =
    draftValueLabels.length === presetValueLabels.length &&
    draftValueLabels.every((label, index) => label === presetValueLabels[index]);

  return (
    draft.question_type === targetType &&
    draft.scale_min === preset.scale_min &&
    draft.scale_max === preset.scale_max &&
    draft.allow_na === preset.allow_na &&
    (draft.display_config.scaleMinLabel?.trim() ?? '') === (preset.scaleMinLabel?.trim() ?? '') &&
    (draft.display_config.scaleMaxLabel?.trim() ?? '') === (preset.scaleMaxLabel?.trim() ?? '') &&
    valueLabelsMatch
  );
}

/** Type + range + N/A only — recovers questions detached by prior label-only preset edits. */
function presetMatchesDraftRange(
  preset: AeTemplateScalePreset,
  draft: Pick<QuestionDraft, 'question_type' | 'scale_min' | 'scale_max' | 'allow_na'>
): boolean {
  const targetType = preset.matrixShared
    ? 'matrix'
    : preset.scale_min === 0 && preset.scale_max === 10
      ? 'nps'
      : 'scale';
  return (
    draft.question_type === targetType &&
    draft.scale_min === preset.scale_min &&
    draft.scale_max === preset.scale_max &&
    draft.allow_na === preset.allow_na
  );
}

function resolveLegacyPresetId(
  draft: Pick<QuestionDraft, 'question_type' | 'scale_min' | 'scale_max' | 'allow_na' | 'display_config'>,
  candidates: AeTemplateScalePreset[]
): string | undefined {
  const strict = candidates.find((p) => presetMatchesDraftValues(p, draft));
  if (strict) return strict.id;
  // If labels already drifted (bug before scalePresetId), rebind when exactly one
  // preset shares type/range/N/A so we do not guess among multiple 1–5 scales.
  const loose = candidates.filter((p) => presetMatchesDraftRange(p, draft));
  return loose.length === 1 ? loose[0].id : undefined;
}

function questionAsDraftForMatch(q: AeTemplateQuestion): QuestionDraft {
  const display = parseDisplayConfig(q.display_config);
  const type =
    q.question_type === 'matrix'
      ? 'matrix'
      : q.question_type === 'nps'
        ? 'nps'
        : 'scale';
  return {
    question_text: q.question_text,
    question_type: type,
    is_required: q.is_required,
    scale_min: q.scale_min ?? 1,
    scale_max: q.scale_max ?? 5,
    allow_na: q.allow_na,
    display_config: display,
    options: [],
  };
}

/** Resolve linked preset id: explicit id, else legacy value match. */
export function resolveScalePresetId(
  draft: Pick<QuestionDraft, 'question_type' | 'scale_min' | 'scale_max' | 'allow_na' | 'display_config'>,
  scalePresets: AeTemplateScalePreset[]
): string | undefined {
  const byId = draft.display_config.scalePresetId?.trim();
  if (byId && scalePresets.some((p) => p.id === byId)) return byId;
  return resolveLegacyPresetId(draft, scalePresets);
}

/** Encodes a selected template scale in the question-type selector. */
export function questionTypeSelectionValue(
  draft: QuestionDraft,
  scalePresets: AeTemplateScalePreset[]
): string {
  const presetId = resolveScalePresetId(draft, scalePresets);
  return presetId ? `scale-preset:${presetId}` : draft.question_type;
}

/**
 * Overlay shared-scale fields from the linked preset onto a draft for display/edit.
 * Keeps per-question overrides: reverseScored and scaleDisplayStyle (except matrix columns).
 */
export function overlayLinkedScalePreset(
  draft: QuestionDraft,
  scalePresets: AeTemplateScalePreset[]
): QuestionDraft {
  const presetId = resolveScalePresetId(draft, scalePresets);
  if (!presetId) return draft;
  const preset = scalePresets.find((p) => p.id === presetId);
  if (!preset) return draft;

  const applied = applyScalePreset(preset);
  const prevDisplay = draft.display_config;
  const nextDisplay: AeQuestionDisplayConfig = {
    ...(applied.display_config ?? {}),
    // Per-question overrides from Survey design
    reverseScored: prevDisplay.reverseScored,
    scaleDisplayStyle:
      applied.question_type === 'matrix'
        ? 'radio'
        : (prevDisplay.scaleDisplayStyle ?? applied.display_config?.scaleDisplayStyle),
    scalePresetId: preset.id,
  };

  return {
    ...draft,
    question_type: (applied.question_type as QuestionDraft['question_type']) ?? draft.question_type,
    scale_min: applied.scale_min ?? draft.scale_min,
    scale_max: applied.scale_max ?? draft.scale_max,
    allow_na: applied.allow_na ?? draft.allow_na,
    display_config: nextDisplay,
  };
}

function collectQuestions(template: AeTemplateDetail): AeTemplateQuestion[] {
  const out: AeTemplateQuestion[] = [];
  const walk = (sections: AeTemplateDetail['sections']) => {
    for (const section of sections) {
      out.push(...(section.questions ?? []));
      if (section.subsections?.length) walk(section.subsections);
    }
  };
  walk(template.sections ?? []);
  return out;
}

function presetScaleFingerprint(preset: AeTemplateScalePreset): string {
  return JSON.stringify({
    matrixShared: !!preset.matrixShared,
    scale_min: preset.scale_min,
    scale_max: preset.scale_max,
    allow_na: preset.allow_na,
    scaleMinLabel: preset.scaleMinLabel ?? '',
    scaleMaxLabel: preset.scaleMaxLabel ?? '',
    scaleValueLabels: effectiveValueLabels(preset.scaleValueLabels),
    scaleDisplayStyle: preset.scaleDisplayStyle ?? '',
  });
}

/**
 * After Setup saves scale presets, re-apply linked (or legacy-matched) questions
 * so take flow and scoring use the updated shared labels/range.
 */
export async function propagateScalePresetsToQuestions(
  templateId: string,
  template: AeTemplateDetail,
  previousPresets: AeTemplateScalePreset[],
  nextPresets: AeTemplateScalePreset[]
): Promise<number> {
  const prevById = new Map(previousPresets.map((p) => [p.id, p]));
  const nextById = new Map(nextPresets.map((p) => [p.id, p]));
  let updated = 0;

  for (const question of collectQuestions(template)) {
    if (
      question.question_type !== 'scale' &&
      question.question_type !== 'nps' &&
      question.question_type !== 'matrix'
    ) {
      continue;
    }

    const draft = questionAsDraftForMatch(question);
    let presetId = draft.display_config.scalePresetId?.trim();
    if (!presetId || !nextById.has(presetId)) {
      // Prefer previous presets (pre-edit values), then next (already-diverged recovery).
      presetId =
        resolveLegacyPresetId(draft, previousPresets) ??
        resolveLegacyPresetId(draft, nextPresets);
    }
    if (!presetId) continue;

    const next = nextById.get(presetId);
    if (!next) continue;

    const prev = prevById.get(presetId);
    const alreadyLinked = draft.display_config.scalePresetId === presetId;
    const unchanged = prev && presetScaleFingerprint(prev) === presetScaleFingerprint(next);
    if (alreadyLinked && unchanged) continue;

    const applied = applyScalePreset(next);
    const prevDisplay = parseDisplayConfig(question.display_config);
    const display_config: AeQuestionDisplayConfig = {
      ...(applied.display_config ?? {}),
      reverseScored: prevDisplay.reverseScored,
      scaleDisplayStyle:
        applied.question_type === 'matrix'
          ? 'radio'
          : (prevDisplay.scaleDisplayStyle ?? applied.display_config?.scaleDisplayStyle),
      scalePresetId: next.id,
    };

    await assessmentEngineService.updateQuestion(templateId, question.id, {
      question_type: applied.question_type,
      scale_min: applied.scale_min ?? null,
      scale_max: applied.scale_max ?? null,
      allow_na: applied.allow_na ?? question.allow_na,
      display_config,
    });
    updated += 1;
  }

  return updated;
}
